import React, { useEffect, useRef, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { XIcon as X, FacebookLogoIcon as FacebookLogo, InstagramLogoIcon as InstagramLogo, TwitterLogoIcon as TwitterLogo, ShareNetworkIcon as ShareNetwork, CopyIcon as Copy, DownloadSimpleIcon as DownloadSimple, SparkleIcon as Sparkle, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { formatDate as _formatDate } from "@/lib/utils-app";

/**
 * First-dog-registration celebration overlay.
 *
 * Renders ONCE per user: the moment they register their first dog,
 * we render this on top of DogDetail with confetti + a polished
 * "registration card" they can download/share. Reads its trigger
 * from localStorage so it survives the Stripe redirect roundtrip.
 *
 * The card is rendered to a <canvas> so we can give the user a real
 * PNG to download (and share on Facebook/Instagram where direct
 * "share this DOM element" doesn't exist on mobile).
 */
export default function FirstDogCelebration({ dog }) {
  const [open, setOpen] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!dog?.id) return;
    let trigger = false;
    try {
      const flag = localStorage.getItem("celebrate_first_dog");
      const pending = localStorage.getItem("celebrate_first_dog_pending");
      if (flag === dog.id || pending === "1") trigger = true;
    } catch { /* localStorage disabled — just skip */ }
    if (!trigger) return;

    setOpen(true);
    // The user just registered their first dog — the single most
    // positive moment in the app. Ask for a store rating (native only,
    // once per install, OS rate-limits the dialog anyway).
    const reviewTimer = setTimeout(() => {
      import("@/lib/appReview").then((m) => m.maybeRequestAppReview("first_dog")).catch(() => {});
    }, 4000);
    // Clear the flags so the celebration never replays — even if
    // the user navigates away and comes back.
    try {
      localStorage.removeItem("celebrate_first_dog");
      localStorage.removeItem("celebrate_first_dog_pending");
    } catch {}
    return () => clearTimeout(reviewTimer);
  }, [dog?.id]);

  useEffect(() => {
    if (!open) return;
    // Draw the shareable registration card to a canvas. Doing this
    // in an effect (vs. on-render) keeps the canvas off the React
    // tree and avoids the "re-render thrash" you get drawing into
    // a ref during render.
    const c = canvasRef.current;
    if (!c) return;
    drawRegistrationCard(c, dog);
  }, [open, dog]);

  if (!open || !dog) return null;

  const downloadCard = () => {
    const c = canvasRef.current;
    if (!c) return;
    const link = document.createElement("a");
    link.download = `${dog.name?.replace(/[^a-zA-Z0-9]+/g, "-") || "registration"}-armstrong-k9.png`;
    link.href = c.toDataURL("image/png");
    link.click();
    toast.success("Saved to your downloads.");
  };

  const copyShareLink = async () => {
    const url = `${webOrigin()}/d/${dog.id}`;
    try {
      await navigator.clipboard?.writeText(url);
      toast.success("Public link copied. Paste anywhere.");
    } catch {
      toast.error("Couldn't copy — try long-pressing to copy the URL bar.");
    }
  };

  const shareNative = async () => {
    const url = `${webOrigin()}/d/${dog.id}`;
    const text = `${dog.name} is officially registered with Armstrong K9 Registry. ${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: dog.name, text, url });
      } catch { /* user cancelled — no toast */ }
    } else {
      // Desktop fallback: copy
      copyShareLink();
    }
  };

  const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(webOrigin() + "/d/" + dog.id)}`;
  const twUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(dog.name + " is officially registered with Armstrong K9 Registry.")}&url=${encodeURIComponent(webOrigin() + "/d/" + dog.id)}`;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-ink/85 backdrop-blur-sm overflow-y-auto"
      data-testid="first-dog-celebration"
      onClick={() => setOpen(false)}
    >
      <Confetti />

      {/* Modal */}
      <div
        className="relative bg-cream rounded-3xl max-w-lg w-full shadow-luxury-heavy overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/80 hover:bg-white text-ink/60 hover:text-ink"
          aria-label="Close"
          data-testid="celebration-close"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="px-7 pt-9 pb-2 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#FBF7EC] border border-[#C9A227]/40 mb-3">
            <Sparkle size={26} weight="duotone" className="text-[#8A6A00]" />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">
            Your first registration
          </p>
          <h2 className="font-spectral font-light text-3xl tracking-tight text-ink">
            {dog.name} is on the registry.
          </h2>
          <p className="text-[14px] text-ink/65 mt-3 leading-relaxed max-w-md mx-auto">
            Permanent. Pedigree-verified. Yours. Share the moment — a
            single post is the warmest introduction your kennel will
            ever get.
          </p>
        </div>

        {/* Shareable card preview */}
        <div className="px-7 pt-5 pb-3">
          <canvas
            ref={canvasRef}
            width="800"
            height="450"
            className="w-full rounded-xl border border-stone-200 shadow-md"
            data-testid="celebration-canvas"
          />
        </div>

        {/* Actions */}
        <div className="px-7 pb-7 pt-3 space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={downloadCard}
              data-testid="celebration-download-card"
              className="bg-ink hover:bg-crimson text-white rounded-full h-11"
            >
              <DownloadSimple size={14} weight="bold" className="mr-1.5" />
              Download card
            </Button>
            <Button
              onClick={shareNative}
              variant="outline"
              data-testid="celebration-share-native"
              className="rounded-full h-11 border-ink/20"
            >
              <ShareNetwork size={14} weight="bold" className="mr-1.5" />
              Share
            </Button>
          </div>
          <div className="flex items-center justify-center gap-3 pt-1">
            <SocialButton
              href={fbUrl}
              icon={FacebookLogo}
              label="Share to Facebook"
              testid="celebration-share-facebook"
            />
            <a
              href={`https://www.instagram.com/armstrongk9registry`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 text-ink/70 hover:text-ink transition-colors"
              aria-label="Share to Instagram (download card first, then post)"
              data-testid="celebration-share-instagram"
              onClick={(e) => {
                if (!canvasRef.current?.toDataURL) return;
                // Instagram doesn't accept web-shared URLs — auto-download
                // the card so the user can attach it in the Instagram app.
                e.preventDefault();
                downloadCard();
                toast.message("Card downloaded — open Instagram and attach it to a new post.");
              }}
            >
              <InstagramLogo size={16} weight="duotone" />
            </a>
            <SocialButton
              href={twUrl}
              icon={TwitterLogo}
              label="Share to X / Twitter"
              testid="celebration-share-twitter"
            />
            <button
              onClick={copyShareLink}
              data-testid="celebration-copy-link"
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 text-ink/70 hover:text-ink transition-colors"
              aria-label="Copy public link"
            >
              <Copy size={16} weight="bold" />
            </button>
          </div>
          <p className="text-[11px] text-ink/50 text-center pt-2">
            Every share is a referral. Two friends who join unlock 1 free month
            of Pro for you.
          </p>
        </div>
      </div>
    </div>
  );
}

function SocialButton({ href, icon: Icon, label, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={testid}
      className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 text-ink/70 hover:text-ink transition-colors"
    >
      <Icon size={16} weight="duotone" />
    </a>
  );
}

/**
 * Drop confetti for ~3 seconds when the modal opens. Pure CSS so
 * nothing to install — 24 colored squares with random animation
 * params, animated by Tailwind keyframes (defined inline below).
 */
function Confetti() {
  const pieces = Array.from({ length: 36 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const duration = 2 + Math.random() * 2;
        const rotate = Math.random() * 360;
        const colors = ["#C9A227", "#C1272D", "#0E0E0E", "#8A6A00", "#F2E9C7", "#FBF7EC"];
        const bg = colors[i % colors.length];
        const size = 6 + Math.random() * 10;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: "-6%",
              left: `${left}%`,
              width: `${size}px`,
              height: `${size * 1.6}px`,
              backgroundColor: bg,
              transform: `rotate(${rotate}deg)`,
              animation: `confettiFall ${duration}s ${delay}s linear forwards`,
              borderRadius: "1px",
            }}
          />
        );
      })}
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(-10vh) rotate(0deg);  opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}

/**
 * Draw the registration card to a canvas. Style: dark cream, gold
 * border, Spectral-ish heading, regulation-style "REG NO." label.
 * Includes the dog photo (if present) clipped into a rounded square
 * on the left, breed/DOB/color on the right.
 */
function drawRegistrationCard(canvas, dog) {
  const ctx = canvas.getContext("2d");
  const w = canvas.width;
  const h = canvas.height;

  // Background
  ctx.fillStyle = "#FAF6EC";
  ctx.fillRect(0, 0, w, h);

  // Gold inner border
  ctx.strokeStyle = "#C9A227";
  ctx.lineWidth = 3;
  ctx.strokeRect(18, 18, w - 36, h - 36);
  ctx.strokeStyle = "rgba(201, 162, 39, 0.35)";
  ctx.lineWidth = 1;
  ctx.strokeRect(28, 28, w - 56, h - 56);

  // Top label
  ctx.fillStyle = "#8A6A00";
  ctx.font = "bold 13px Inter, system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("ARMSTRONG K9 REGISTRY · OFFICIAL", 56, 64);

  // Photo
  const photoUrl = dog.photos?.[0];
  const photoBox = { x: 56, y: 90, size: 280 };
  ctx.fillStyle = "#0E0E0E";
  roundedRect(ctx, photoBox.x, photoBox.y, photoBox.size, photoBox.size, 12);
  ctx.fill();

  // Dog name + meta on the right
  const textX = 380;
  ctx.fillStyle = "#0E0E0E";
  ctx.font = "300 36px 'Spectral', 'Cormorant Garamond', Georgia, serif";
  wrapText(ctx, dog.name || "Unnamed", textX, 130, 350, 40);

  ctx.fillStyle = "rgba(14,14,14,0.6)";
  ctx.font = "16px Inter, system-ui, sans-serif";
  let lineY = 220;
  const lines = [
    dog.breed || "—",
    dog.sex ? `${cap(dog.sex)} · ${dog.color || "—"}` : (dog.color || ""),
    dog.dob ? `Born ${fmtDate(dog.dob)}` : "",
  ].filter(Boolean);
  for (const line of lines) {
    ctx.fillText(line, textX, lineY);
    lineY += 24;
  }

  // Registration number
  ctx.fillStyle = "#8A6A00";
  ctx.font = "bold 11px Inter, system-ui, sans-serif";
  ctx.fillText("REG. NO.", textX, 320);
  ctx.fillStyle = "#0E0E0E";
  ctx.font = "bold 20px 'JetBrains Mono', 'Courier New', monospace";
  ctx.fillText(dog.registration_number || dog.id?.slice(0, 16).toUpperCase() || "—", textX, 348);

  // Footer
  ctx.fillStyle = "rgba(14,14,14,0.45)";
  ctx.font = "11px Inter, system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.fillText("armstrongk9registry.com", 56, h - 50);
  ctx.textAlign = "right";
  ctx.fillText(`Verified ${fmtDate(new Date().toISOString())}`, w - 56, h - 50);

  // Try to load + draw the photo over the dark placeholder. Done in
  // a setTimeout so the layout is on-screen first; the image fills
  // in once it loads. Cross-origin photos may not render if their
  // host doesn't allow CORS — that's why we keep the dark placeholder
  // (always looks intentional).
  if (photoUrl) {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.save();
      roundedRect(ctx, photoBox.x, photoBox.y, photoBox.size, photoBox.size, 12);
      ctx.clip();
      // object-cover behaviour
      const ratio = img.width / img.height;
      let drawW = photoBox.size, drawH = photoBox.size;
      if (ratio > 1) { drawW = photoBox.size * ratio; }
      else { drawH = photoBox.size / ratio; }
      const dx = photoBox.x - (drawW - photoBox.size) / 2;
      const dy = photoBox.y - (drawH - photoBox.size) / 2;
      ctx.drawImage(img, dx, dy, drawW, drawH);
      ctx.restore();
    };
    img.src = photoUrl;
  }
}

function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrapText(ctx, text, x, y, maxW, lineH) {
  const words = text.split(" ");
  let line = "";
  let curY = y;
  for (const word of words) {
    const test = line ? line + " " + word : word;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, curY);
      line = word;
      curY += lineH;
    } else { line = test; }
  }
  ctx.fillText(line, x, curY);
}

function cap(s) { return s ? s[0].toUpperCase() + s.slice(1) : ""; }
// Delegate to the shared helper so DOB date-only values (YYYY-MM-DD)
// don't shift by one day when the viewer is west of UTC. The shared
// helper already handles both date-only and full timestamps.
function fmtDate(iso) {
  if (!iso) return "";
  return _formatDate(iso);
}
